import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { motion, useScroll, AnimatePresence } from "framer-motion";
import { LogOut, User, Menu, X, Sun, Moon, ChevronDown, Command, ShieldCheck, TerminalSquare, Target, Briefcase, LifeBuoy, Search, Server, Newspaper, FileSearch, UserRound } from "lucide-react";
import api from "@/lib/api";
import CurrencyPicker from "@/components/CurrencyPicker";

/* Hub-wide search — servers, citizens, bulletins, closed case files. One box, whole city. */
function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    const t = setTimeout(() => api.get("/search", { params: { q: q.trim() } }).then((r) => setRes(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const go = (path) => { setOpen(false); setQ(""); setRes(null); navigate(path); };
  const empty = res && !res.servers.length && !res.citizens.length && !res.bulletins.length && !res.cases.length;

  return (
    <div ref={ref} className="relative hidden md:block">
      <button data-testid="global-search-open" onClick={() => setOpen((o) => !o)} title="Search the city"
        className="w-9 h-9 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-nc-blue hover:border-nc-blue/50 transition-colors duration-300">
        <Search className="w-4 h-4" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1.5 w-[340px] border border-white/15 bg-[#0a0a0a]/95 backdrop-blur-md z-[80] overflow-hidden" style={{ boxShadow: "0 20px 50px rgba(0,0,0,0.7)" }}>
          <input data-testid="global-search-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search servers, citizens, news, case files…"
            className="w-full bg-transparent border-b border-white/10 px-4 py-3 font-mono text-[11px] text-zinc-100 placeholder:text-zinc-600 focus:outline-none [color-scheme:dark]" />
          {res && (
            <div className="max-h-[320px] overflow-y-auto" style={{ scrollbarWidth: "thin" }}>
              {empty && <p className="font-mono text-[10px] text-zinc-500 px-4 py-4">Nothing in the city matches that.</p>}
              {res.servers.length > 0 && (
                <div>
                  <p className="font-mono text-[7px] uppercase tracking-[0.25em] text-zinc-600 px-4 pt-2.5 pb-1">Servers</p>
                  {res.servers.map((s) => (
                    <button key={s.id} data-testid="search-result-server" onClick={() => go("/new-citizen/servers")}
                      className="w-full text-left px-4 py-2 hover:bg-white/[0.04] transition-colors flex items-center gap-2.5">
                      <Server className="w-3 h-3 text-nc-blue shrink-0" />
                      <span className="font-mono text-[10px] text-zinc-200 truncate">{s.name}</span>
                      {s.tagline && <span className="font-mono text-[8px] text-zinc-600 truncate ml-auto">{s.tagline}</span>}
                    </button>
                  ))}
                </div>
              )}
              {res.citizens.length > 0 && (
                <div>
                  <p className="font-mono text-[7px] uppercase tracking-[0.25em] text-zinc-600 px-4 pt-2.5 pb-1">Citizens</p>
                  {res.citizens.map((c) => (
                    <button key={c.id} data-testid="search-result-citizen" onClick={() => go(`/community/${c.id}`)}
                      className="w-full text-left px-4 py-2 hover:bg-white/[0.04] transition-colors flex items-center gap-2.5">
                      <UserRound className="w-3 h-3 text-emerald-400 shrink-0" />
                      <span className="font-mono text-[10px] text-zinc-200 truncate">{c.name}</span>
                      {c.bio && <span className="font-mono text-[8px] text-zinc-600 truncate ml-auto">{c.bio}</span>}
                    </button>
                  ))}
                </div>
              )}
              {res.bulletins.length > 0 && (
                <div>
                  <p className="font-mono text-[7px] uppercase tracking-[0.25em] text-zinc-600 px-4 pt-2.5 pb-1">Bulletins</p>
                  {res.bulletins.map((b) => (
                    <button key={b.id} data-testid="search-result-bulletin" onClick={() => go("/new-citizen/hub")}
                      className="w-full text-left px-4 py-2 hover:bg-white/[0.04] transition-colors flex items-center gap-2.5">
                      <Newspaper className="w-3 h-3 text-nc-orange shrink-0" />
                      <span className="font-mono text-[10px] text-zinc-200 truncate">{b.title}</span>
                    </button>
                  ))}
                </div>
              )}
              {res.cases.length > 0 && (
                <div>
                  <p className="font-mono text-[7px] uppercase tracking-[0.25em] text-zinc-600 px-4 pt-2.5 pb-1">Case files</p>
                  {res.cases.map((c) => (
                    <button key={c.id} data-testid="search-result-case" onClick={() => go("/new-citizen/case-files")}
                      className="w-full text-left px-4 py-2 hover:bg-white/[0.04] transition-colors flex items-center gap-2.5">
                      <FileSearch className="w-3 h-3 text-amber-400 shrink-0" />
                      <span className="font-mono text-[10px] text-zinc-200 truncate">{c.title}</span>
                      <span className="font-mono text-[8px] text-zinc-600 ml-auto">{c.status === "solved" ? "closed" : "cold"}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  return (
    <motion.div
      data-testid="scroll-progress"
      className="fixed top-0 left-0 right-0 h-[2px] bg-nc-blue origin-left z-[70]"
      style={{ scaleX: scrollYProgress, boxShadow: "0 0 12px rgba(46, 107, 255, 0.5)" }}
    />
  );
}

export function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem("sp_theme") || "dark");
  useEffect(() => {
    document.documentElement.classList.toggle("light", theme === "light");
    localStorage.setItem("sp_theme", theme);
  }, [theme]);
  return [theme, setTheme];
}

export function ThemeToggle({ className = "" }) {
  const [theme, setTheme] = useTheme();
  const light = theme === "light";
  return (
    <button
      data-testid="theme-toggle-button"
      onClick={() => setTheme(light ? "dark" : "light")}
      title={light ? "Switch to dark mode" : "Switch to light mode"}
      className={`w-9 h-9 border border-white/10 flex items-center justify-center text-zinc-400 hover:text-nc-orange hover:border-nc-orange/50 transition-colors duration-300 ${className}`}
    >
      {light ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
    </button>
  );
}

export const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/new-citizen", label: "New Citizen" },
  { to: "/new-citizen/hub", label: "Citizen Hub" },
  { to: "/new-citizen/servers", label: "NC Servers" },
  { to: "/new-citizen/wiki", label: "NC Wiki" },
  { to: "/modhub", label: "Mod Hub" },
  { to: "/new-citizen/workshop", label: "Mod Workshop" },
  { to: "/asset-packs", label: "Asset Packs" },
  { to: "/services", label: "Services" },
  { to: "/jobs", label: "Job Market" },
  { to: "/gallery", label: "Gallery" },
  { to: "/marketplace", label: "Marketplace" },
  { to: "/portfolio", label: "Portfolio" },
  { to: "/team", label: "Team" },
  { to: "/news", label: "News" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

const NAV_TOP = [
  { to: "/", label: "Home" },
];

const NAV_GROUPS = [
  {
    label: "New Citizen",
    links: [
      { to: "/new-citizen/hub", label: "Citizen Hub ★" },
      { to: "/new-citizen", label: "Roadmap" },
      { to: "/new-citizen/servers", label: "Servers & Community" },
      { to: "/admin/hub", label: "Server Deck (Owners)" },
      { to: "/new-citizen/wiki", label: "Game Wiki" },
      { to: "/modhub", label: "Mod Hub" },
      { to: "/new-citizen/workshop", label: "Mod Workshop" },
      { href: "https://discord.gg/NPhbFVSmpd", label: "Discord ↗" },
    ],
  },
  {
    label: "Work",
    links: [
      { to: "/services", label: "Services" },
      { to: "/marketplace", label: "Marketplace" },
      { to: "/directory", label: "Contractor Network" },
      { to: "/join-creators", label: "Creator Program" },
      { to: "/portfolio", label: "Portfolio" },
      { to: "/asset-packs", label: "Asset Packs" },
    ],
  },
  {
    label: "Studio",
    links: [
      { to: "/team", label: "Team" },
      { to: "/news", label: "News" },
      { to: "/careers", label: "Careers" },
      { to: "/contact", label: "Contact" },
    ],
  },
];

function NavDropdown({ group }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  const active = group.links.some((l) => location.pathname === l.to);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <div ref={ref} className="relative">
      <button
        data-testid={`nav-dropdown-${group.label.toLowerCase().replace(/\s+/g, "-")}`}
        onClick={() => setOpen((o) => !o)}
        className={`font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 transition-colors duration-300 flex items-center gap-1.5 ${
          active ? "text-nc-blue bg-nc-blue/10" : "text-zinc-400 hover:text-zinc-100"
        }`}
      >
        {group.label}
        <motion.span animate={{ rotate: open ? 180 : 0 }} transition={{ type: "spring", stiffness: 500, damping: 25 }}>
          <ChevronDown className="w-3 h-3" />
        </motion.span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={`nav-dropdown-panel-${group.label.toLowerCase().replace(/\s+/g, "-")}`}
            className="absolute left-0 top-full mt-1 min-w-[190px] border border-white/15 bg-[#0a0a0a]/95 backdrop-blur-md z-[80] overflow-hidden"
            style={{ boxShadow: "0 20px 50px rgba(0,0,0,0.7), 0 0 30px rgba(46,107,255,0.08)", transformOrigin: "top left" }}
            initial={{ opacity: 0, scaleY: 0.6, y: -6 }}
            animate={{ opacity: 1, scaleY: 1, y: 0 }}
            exit={{ opacity: 0, scaleY: 0.7, y: -4, transition: { duration: 0.14, ease: "easeIn" } }}
            transition={{ type: "spring", stiffness: 420, damping: 28 }}
          >
            <motion.div
              className="h-[2px] bg-gradient-to-r from-nc-blue via-nc-orange to-transparent"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              style={{ transformOrigin: "left" }}
            />
            {group.links.map((l, i) => (
              <motion.div
                key={l.to || l.href}
                initial={{ opacity: 0, x: -14 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -8, transition: { duration: 0.08 } }}
                transition={{ delay: 0.05 + i * 0.04, type: "spring", stiffness: 480, damping: 30 }}
              >
                {l.href ? (
                  <a
                    href={l.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group/link relative block font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 border-b border-white/5 last:border-0 text-zinc-400 hover:text-zinc-100 transition-colors duration-200"
                  >
                    <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-nc-blue scale-y-0 group-hover/link:scale-y-100 transition-transform duration-200 origin-top" />
                    <span className="inline-block group-hover/link:translate-x-1.5 transition-transform duration-200">{l.label}</span>
                  </a>
                ) : (
                <NavLink
                  to={l.to}
                  end
                  data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `group/link relative block font-mono text-[10px] tracking-[0.2em] uppercase px-4 py-2.5 border-b border-white/5 last:border-0 transition-colors duration-200 ${
                      isActive ? "text-nc-blue bg-nc-blue/10" : "text-zinc-400 hover:text-zinc-100"
                    }`
                  }
                >
                  <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-nc-blue scale-y-0 group-hover/link:scale-y-100 transition-transform duration-200 origin-top" />
                  <span className="inline-block group-hover/link:translate-x-1.5 transition-transform duration-200">{l.label}</span>
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-nc-blue opacity-0 group-hover/link:opacity-100 transition-opacity duration-200">→</span>
                </NavLink>
                )}
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const ACCOUNT_ROLE_META = {
  admin: { label: "Admin // L5", dot: "bg-nc-orange", text: "text-nc-orange", ring: "border-nc-orange/60" },
  dev: { label: "Dev", dot: "bg-nc-blue", text: "text-nc-blue", ring: "border-nc-blue/60" },
  sales: { label: "Sales", dot: "bg-nc-blue", text: "text-nc-blue", ring: "border-nc-blue/60" },
  client: { label: "Client", dot: "bg-nc-green", text: "text-nc-green", ring: "border-nc-green/60" },
  citizen: { label: "Citizen", dot: "bg-zinc-500", text: "text-zinc-400", ring: "border-white/25" },
};

function AccountMenu({ user, onLogout }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const location = useLocation();
  const meta = ACCOUNT_ROLE_META[user.role] || ACCOUNT_ROLE_META.citizen;
  const initial = (user.name || user.email || "?").slice(0, 1).toUpperCase();

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const consoles = [
    { to: "/command", label: "Projects", icon: Command, show: true },
    { to: "/admin", label: "Admin Panel", icon: ShieldCheck, show: user.role === "admin" },
    { to: "/dev-hub", label: "Dev Hub", icon: TerminalSquare, show: ["dev", "admin"].includes(user.role) },
    { to: "/sales", label: "Sales Dashboard", icon: Target, show: ["sales", "admin"].includes(user.role) },
    { to: "/portal", label: "Client Portal", icon: Briefcase, show: ["client", "sales"].includes(user.role) },
  ];
  const services = [
    { to: "/profile", label: "Profile & Referral Earnings", icon: User },
    { to: "/support", label: "Support Desk", icon: LifeBuoy },
  ];

  return (
    <div ref={ref} className="relative hidden sm:block">
      <button
        data-testid="nav-account-button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2.5 border border-white/10 hover:border-white/30 bg-black/40 pl-1.5 pr-3 py-1.5 transition-colors duration-300"
      >
        <span className={`w-8 h-8 rounded-full border ${meta.ring} bg-[#0a0a0a] flex items-center justify-center font-sans font-bold text-sm ${meta.text}`}>
          {initial}
        </span>
        <span className="flex flex-col items-start leading-none">
          <span className="font-mono text-[10px] text-zinc-100 max-w-[110px] truncate">{user.name || user.email}</span>
          <span className={`font-mono text-[8px] tracking-[0.2em] uppercase mt-1 flex items-center gap-1.5 ${meta.text}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${meta.dot} animate-pulse`} /> {meta.label}
          </span>
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-account-panel"
            className="absolute right-0 top-full mt-1.5 w-[250px] border border-white/15 bg-[#0a0a0a]/95 backdrop-blur-md z-[80] overflow-hidden"
            style={{ boxShadow: "0 20px 50px rgba(0,0,0,0.7), 0 0 30px rgba(46,107,255,0.08)", transformOrigin: "top right" }}
            initial={{ opacity: 0, scale: 0.92, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -6, transition: { duration: 0.13, ease: "easeIn" } }}
            transition={{ type: "spring", stiffness: 400, damping: 26 }}
          >
            <motion.div className="h-[2px] bg-gradient-to-r from-nc-orange via-nc-blue to-transparent" initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 0.35 }} style={{ transformOrigin: "left" }} />
            <div className="border-b border-white/10 px-4 py-3">
              <p className="font-mono text-[8px] tracking-[0.3em] text-zinc-500 uppercase">Signed in as</p>
              <p className="font-mono text-[10px] text-zinc-100 truncate mt-1">{user.email}</p>
            </div>
            <div className="py-1.5">
              <p className="font-mono text-[8px] tracking-[0.3em] text-zinc-600 uppercase px-4 pt-1.5 pb-1">Consoles</p>
              {consoles.filter((c) => c.show).map((c, i) => (
                <motion.div key={c.to} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, transition: { duration: 0.06 } }} transition={{ delay: 0.04 + i * 0.04, type: "spring", stiffness: 460, damping: 28 }}>
                  <Link to={c.to} data-testid={`account-link-${c.label.toLowerCase().replace(/\s+/g, "-")}`} className="group/link relative flex items-center gap-2.5 px-4 py-2 font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-300 hover:text-nc-blue transition-colors duration-200">
                    <span className="absolute left-0 top-0 bottom-0 w-[2px] bg-nc-blue scale-y-0 group-hover/link:scale-y-100 transition-transform duration-200 origin-top" />
                    <c.icon className="w-3.5 h-3.5 text-zinc-500 group-hover/link:text-nc-blue transition-colors duration-200" />
                    <span className="group-hover/link:translate-x-1 transition-transform duration-200">{c.label}</span>
                  </Link>
                </motion.div>
              ))}
            </div>
            <div className="py-1.5 border-t border-white/10">
              <p className="font-mono text-[8px] tracking-[0.3em] text-zinc-600 uppercase px-4 pt-1.5 pb-1">Account</p>
              {services.map((s, i) => (
                <motion.div key={s.to} initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, transition: { duration: 0.06 } }} transition={{ delay: 0.2 + i * 0.04, type: "spring", stiffness: 460, damping: 28 }}>
                  <Link to={s.to} data-testid={`account-link-${s.label.toLowerCase().replace(/\s+/g, "-")}`} className="group/link flex items-center gap-2.5 px-4 py-2 font-mono text-[10px] tracking-[0.15em] uppercase text-zinc-300 hover:text-nc-blue transition-colors duration-200">
                    <s.icon className="w-3.5 h-3.5 text-zinc-500 group-hover/link:text-nc-blue transition-colors duration-200" />
                    <span className="group-hover/link:translate-x-1 transition-transform duration-200">{s.label}</span>
                  </Link>
                </motion.div>
              ))}
              <motion.button
                data-testid="nav-logout-button"
                onClick={onLogout}
                className="w-full flex items-center gap-2.5 px-4 py-2 font-mono text-[10px] tracking-[0.15em] uppercase text-red-400/90 hover:text-red-400 hover:bg-red-400/5 transition-colors duration-200"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ delay: 0.28 }}
              >
                <LogOut className="w-3.5 h-3.5" /> Sign Out
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function SiteNav() {
  const navigate = useNavigate();
  const [roadmap, setRoadmap] = useState(null);
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nc_user")); } catch { return null; }
  });

  useEffect(() => {
    api.get("/roadmap").then((r) => setRoadmap(r.data)).catch(() => {});
  }, []);

  const links = (roadmap?.links || []).filter((l) => l.url);
  const steam = links.find((l) => l.label.toUpperCase().includes("STEAM"));

  const handleLogout = () => {
    localStorage.removeItem("nc_token");
    localStorage.removeItem("nc_user");
    setUser(null);
    navigate("/");
  };

  return (
    <nav data-testid="site-nav" className="sticky top-0 z-50 border-b border-white/10 bg-[#050505]/95 backdrop-blur-md">
      <ScrollProgress />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/" data-testid="nav-brand" className="flex items-center gap-3 group shrink-0">
          <img src="/assets/specpixel-logo-transparent.png" alt="Specpixel Entertainment" className="brand-mark h-10 w-10 rounded-full group-hover:opacity-90 transition-opacity duration-300" />
          <div className="hidden sm:flex flex-col">
            <span className="font-sans font-bold text-sm tracking-[0.2em] text-zinc-100 leading-none">SPECPIXEL</span>
            <span className="font-mono text-[8px] tracking-[0.35em] text-zinc-500 leading-none mt-1">ENTERTAINMENT</span>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {NAV_TOP.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `font-mono text-[10px] tracking-[0.2em] uppercase px-3 py-2 transition-colors duration-300 ${
                  isActive ? "text-nc-blue bg-nc-blue/10" : "text-zinc-400 hover:text-zinc-100"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          {NAV_GROUPS.map((g) => <NavDropdown key={g.label} group={g} />)}
        </div>

        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
          <GlobalSearch />
          <div className="hidden md:block"><CurrencyPicker /></div>
          <ThemeToggle />
          {user ? (
            <AccountMenu user={user} onLogout={handleLogout} />
          ) : (
            <Link to="/auth" data-testid="nav-login-button" className="hidden sm:block font-mono text-[10px] tracking-[0.2em] uppercase text-zinc-300 hover:text-nc-blue border border-white/15 hover:border-nc-blue/50 px-3 py-2.5 transition-colors duration-300">
              Enlist
            </Link>
          )}
          <Link to="/contact" data-testid="nav-quote-button" className="hidden xl:block bg-nc-blue hover:bg-nc-blue/80 text-white font-sans font-semibold uppercase tracking-[0.15em] text-xs px-4 py-2.5 transition-colors duration-300" style={{ boxShadow: "0 0 18px rgba(46,107,255,0.25)" }}>
            Get a Quote
          </Link>
          {steam && (
            <a data-testid="nav-steam-button" href={steam.url || "https://store.steampowered.com/app/2019700/"} target="_blank" rel="noopener noreferrer" className="hidden md:block bg-nc-orange hover:bg-nc-orange/80 text-white font-sans font-semibold uppercase tracking-[0.15em] text-xs px-4 py-2.5 transition-colors duration-300">
              Play on Steam
            </a>
          )}
          <button data-testid="nav-menu-button" onClick={() => setOpen(!open)} className="lg:hidden w-9 h-9 border border-white/10 flex items-center justify-center text-zinc-400">
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="nav-mobile-menu" className="lg:hidden border-t border-white/10 bg-[#050505] px-4 py-3 flex flex-col gap-1">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `font-mono text-xs tracking-[0.2em] uppercase px-3 py-2.5 transition-colors duration-300 ${
                  isActive ? "text-nc-blue bg-nc-blue/10" : "text-zinc-400 hover:text-zinc-100"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div className="border-t border-white/10 mt-2 pt-2">
            <div className="px-3 pb-2"><CurrencyPicker compact /></div>
            {user ? (
              <>
                {(user.role === "dev" || user.role === "admin") && (
                  <Link to="/dev-hub" onClick={() => setOpen(false)} className="font-mono text-xs tracking-[0.2em] uppercase text-nc-orange px-3 py-2.5 block">
                    Dev Hub
                  </Link>
                )}
                {(user.role === "sales" || user.role === "admin") && (
                  <Link to="/sales" onClick={() => setOpen(false)} className="font-mono text-xs tracking-[0.2em] uppercase text-nc-blue px-3 py-2.5 block">
                    Sales Dashboard
                  </Link>
                )}
                {(user.role === "client" || user.role === "sales") && (
                  <Link to="/portal" onClick={() => setOpen(false)} className="font-mono text-xs tracking-[0.2em] uppercase text-nc-green px-3 py-2.5 block">
                    Client Portal
                  </Link>
                )}
                <Link to="/command" onClick={() => setOpen(false)} className="font-mono text-xs tracking-[0.2em] uppercase text-nc-orange px-3 py-2.5 block">
                  Projects
                </Link>
                <button data-testid="nav-mobile-logout" onClick={handleLogout} className="font-mono text-xs tracking-[0.2em] uppercase text-red-400 px-3 py-2.5">
                  Logout ({user.name || user.email})
                </button>
              </>
            ) : (
              <Link to="/auth" onClick={() => setOpen(false)} className="font-mono text-xs tracking-[0.2em] uppercase text-nc-blue px-3 py-2.5 block">
                Enlist / Login
              </Link>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
